<template>
  <section class="personnel-kpi-container">
    <div class="module-header">
      <div class="header-main">
        <span class="module-icon"><i class="el-icon-data-analysis" /></span>
        <div>
          <div class="title-line">
            <h2>Thống Kê KPI Nhân Sự</h2>
          </div>
          <p>Chọn nhóm dữ liệu và phạm vi thống kê để định hình chỉ số KPI nhân sự.</p>
        </div>
      </div>
    </div>

    <div class="person-filter">
      <div class="filter-intro">
        <span class="filter-icon"><i class="el-icon-user" /></span>
        <div>
          <h3>Xem KPI theo nhân viên</h3>
          <p>Chọn nhân viên trong danh sách của trường để xem KPI cá nhân.</p>
        </div>
      </div>
      <div class="filter-controls">
        <label class="filter-field">
          <span>Phòng ban</span>
          <el-select v-model="selectedDepartment" size="small" placeholder="Chọn phòng ban" @change="onDepartmentChange">
            <el-option v-for="department in departments" :key="department.id" :label="department.departmentName" :value="department.id" />
          </el-select>
        </label>
        <label class="filter-field employee-field">
          <span>Nhân viên</span>
          <el-select ref="employeeSelect" v-model="selectedEmployee" size="small" filterable remote default-first-option clearable
            :remote-method="filterEmployeeOptions" placeholder="Gõ tên nhân viên..." :loading="loadingEmployees"
            :disabled="loadingEmployees || !filteredEmployees.length" @change="onEmployeeChange"
            @keydown.enter.native.capture="searchEmployeesOnEnter">
            <el-option v-for="employee in employeeSearchResults" :key="employee.id" :label="employee.name" :value="employee.id" />
            <el-option v-if="employeeSearchQuery && !employeeSearchResults.length" disabled label="Không tìm thấy nhân viên" value="" />
          </el-select>
        </label>
      </div>
      <div class="selected-person">
        <span class="person-avatar">{{ currentEmployee.initials }}</span>
        <span><strong>{{ currentEmployee.name }}</strong><small>{{ currentDepartmentName }}</small></span>
      </div>
    </div>

    <div class="source-panel">
      <h3>Nhóm dữ liệu dự kiến</h3>
      <div class="source-grid">
        <button
          v-for="source in sources"
          :key="source.key"
          type="button"
          :class="['source-card', { selected: selectedSource === source.key }]"
          :style="{ borderLeftColor: source.color }"
          :aria-pressed="selectedSource === source.key"
          @click="selectSource(source.key)"
        >
          <span class="source-icon" :style="{ color: source.color, backgroundColor: source.background }">
            <i :class="source.icon" />
          </span>
          <span class="source-copy">
            <strong>{{ source.title }}</strong>
            <small>{{ source.description }}</small>
          </span>
        </button>
      </div>
    </div>

    <div class="chart-panel">
      <div class="panel-heading">
        <div>
          <h3>{{ currentSource.title }} · {{ currentEmployee.name }}</h3>
          <p>Nhân viên: <strong>{{ currentEmployee.name }}</strong> · Nhóm dữ liệu: <strong>{{ currentSource.title }}</strong></p>
        </div>
        <div class="chart-heading-actions">
          <span v-if="selectedSource === 'ATTENDANCE' && loadingAttendance" class="discussion-badge">Đang tải dữ liệu</span>
          <span v-else-if="selectedSource === 'ATTENDANCE' && attendanceData" class="live-data-badge">Dữ liệu từ hệ thống</span>
          <span v-else-if="selectedSource === 'STUDENT_ATTENDANCE' && loadingStudentAttendance" class="discussion-badge">Đang tải dữ liệu</span>
          <span v-else-if="selectedSource === 'STUDENT_ATTENDANCE' && studentAttendanceData" class="live-data-badge">Dữ liệu từ hệ thống</span>
          <span v-else-if="selectedSource === 'TIMETABLE' && loadingTimetable" class="discussion-badge">Đang tải dữ liệu</span>
          <span v-else-if="selectedSource === 'TIMETABLE' && timetableData" class="live-data-badge">Dữ liệu từ hệ thống</span>
          <span v-else-if="selectedSource === 'EVALUATION' && loadingEvaluation" class="discussion-badge">Đang tải dữ liệu</span>
          <span v-else-if="selectedSource === 'EVALUATION' && evaluationData" class="live-data-badge">Dữ liệu từ hệ thống</span>
          <span v-else-if="selectedSource === 'MENU' && loadingMenuKpi" class="discussion-badge">Đang tải dữ liệu</span>
          <span v-else-if="selectedSource === 'MENU' && menuKpiData" class="live-data-badge">Dữ liệu từ hệ thống</span>
          <span v-else-if="selectedSource === 'ALBUM' && loadingAlbumKpi" class="discussion-badge">Đang tải dữ liệu</span>
          <span v-else-if="selectedSource === 'ALBUM' && albumKpiData" class="live-data-badge">Dữ liệu từ hệ thống</span>
          <div v-if="selectedSource !== 'EVALUATION'" class="scope-filter" aria-label="Phạm vi thống kê">
            <span class="scope-label"><i class="el-icon-date" /> Phạm vi thống kê:</span>
            <div class="scope-options">
              <button
                v-for="scope in availableScopes"
                :key="scope.value"
                type="button"
                :class="['scope-button', { active: activeScope === scope.value }]"
                :aria-pressed="activeScope === scope.value"
                @click="changeScope(scope.value)"
              >{{ scope.label }}</button>
            </div>
          </div>
          <div v-else class="evaluation-filter" aria-label="Loại nhận xét">
            <button v-for="type in evaluationTypes" :key="type.value" type="button"
              :class="['evaluation-filter-button', { active: evaluationType === type.value }]"
              :aria-pressed="evaluationType === type.value" @click="evaluationType = type.value">{{ type.label }}</button>
          </div>
          <div v-if="selectedSource !== 'EVALUATION'" class="period-picker">
            <el-date-picker v-if="activeScope === 'DAY'" key="personnel-day-picker" v-model="selectedDayDate" type="date" size="small"
              format="dd/MM/yyyy" placeholder="Chọn ngày" :clearable="false"
              align="right" :append-to-body="true"
              :picker-options="['STUDENT_ATTENDANCE', 'ALBUM'].includes(selectedSource) ? studentAttendanceDatePickerOptions : undefined"
              @change="resetAttendanceDay" />
            <el-date-picker v-if="activeScope === 'WEEK'" key="personnel-week-picker" v-model="selectedWeekDate" type="week" size="small"
              format="Tuần WW (yyyy)" placeholder="Chọn tuần" :clearable="false"
              align="right" :append-to-body="true" :picker-options="selectedSource === 'ALBUM' ? albumWeekPickerOptions : weekPickerOptions" @change="resetAttendanceDay" />
            <el-date-picker v-if="activeScope === 'MONTH'" key="personnel-month-picker" v-model="selectedMonthDate" type="month" size="small"
              format="MM/yyyy" placeholder="Chọn tháng" :clearable="false"
              align="right" :append-to-body="true" :picker-options="selectedSource === 'ALBUM' ? albumMonthPickerOptions : undefined" @change="resetAttendanceDay" />
            <el-date-picker v-if="activeScope === 'RANGE'" key="personnel-range-picker" v-model="selectedDateRange" type="daterange" size="small"
              format="dd/MM/yyyy" range-separator="-" start-placeholder="Từ ngày" end-placeholder="Đến ngày"
              :clearable="false" align="right" :append-to-body="true" :unlink-panels="true"
              :picker-options="selectedSource === 'ALBUM' ? albumRangePickerOptions : undefined" @change="resetAttendanceDay" />
          </div>
          <div v-else class="evaluation-period-picker">
            <el-date-picker v-if="evaluationType === 'DAILY'" key="evaluation-day-picker" v-model="selectedEvaluationDate" type="date" size="small"
              format="dd/MM/yyyy" value-format="yyyy-MM-dd" placeholder="Chọn ngày nhận xét"
              :clearable="false" :picker-options="evaluationDatePickerOptions" />
            <el-date-picker v-else-if="evaluationType === 'WEEKLY'" key="evaluation-week-picker" v-model="selectedEvaluationWeek" type="week" size="small"
              format="Tuần WW (yyyy)" placeholder="Chọn tuần nhận xét"
              :clearable="false" :picker-options="evaluationDatePickerOptions" />
            <el-date-picker v-else-if="evaluationType === 'MONTHLY'" key="evaluation-month-picker" v-model="selectedEvaluationMonth" type="month" size="small"
              format="MM/yyyy" value-format="yyyy-MM" placeholder="Chọn tháng nhận xét"
              :clearable="false" :picker-options="evaluationDatePickerOptions" />
            <template v-else>
              <el-date-picker key="evaluation-year-picker" v-model="selectedEvaluationYear" type="year" size="small" format="yyyy" value-format="yyyy"
                placeholder="Năm dương lịch" :clearable="false" :picker-options="evaluationYearPickerOptions"
                @change="updateEvaluationAnchor" />
              <el-input-number v-model="selectedEvaluationPeriodNumber" size="small" :min="1" :max="99"
                controls-position="right" placeholder="Đợt" />
              <el-date-picker key="evaluation-anchor-picker" v-model="selectedEvaluationAnchorDate" type="date" size="small" format="dd/MM/yyyy"
                value-format="yyyy-MM-dd" placeholder="Ngày mốc" :clearable="false"
                :picker-options="evaluationAnchorPickerOptions" />
            </template>
          </div>
          <div v-if="!isRatioSource && selectedSource !== 'ATTENDANCE'" class="chart-type-options" aria-label="Loại biểu đồ">
            <button type="button" :class="{ active: chartType === 'BAR' }" :aria-pressed="chartType === 'BAR'" @click="chartType = 'BAR'"><i class="el-icon-data-analysis" /> Cột</button>
            <button type="button" :class="{ active: chartType === 'PIE' }" :aria-pressed="chartType === 'PIE'" @click="chartType = 'PIE'"><i class="el-icon-pie-chart" /> Tròn</button>
          </div>
        </div>
      </div>

      <div v-if="selectedSource === 'ATTENDANCE'" class="attendance-chart" v-loading="loadingAttendance">
        <div v-if="attendanceError" class="attendance-load-state error-state"><i class="el-icon-warning-outline" /><span>{{ attendanceError }}</span></div>
        <div v-else-if="!attendanceData && !loadingAttendance" class="attendance-load-state"><i class="el-icon-info" /><span>Chọn nhân viên và khoảng ngày để tải số liệu chấm công.</span></div>
        <template v-else-if="attendanceData">
        <div class="chart-caption">
          <strong>Chấm công của {{ currentEmployee.name }}</strong>
          <span>{{ activeScope === 'DAY' ? 'Chi tiết trạng thái và giờ chấm công' : periodLabel + ' · ' + attendanceData.elapsedDays + ' ngày đã qua' }}</span>
        </div>

        <div v-if="activeScope !== 'DAY'" class="attendance-overview">
          <div class="attendance-donut-card">
            <div class="attendance-donut-wrap">
              <div class="donut" :style="attendancePieStyle">
                <div class="donut-hole"><strong>{{ attendanceRecordedDayCount }}</strong><small>ngày tính tỷ lệ</small></div>
              </div>
              <span v-for="item in attendancePieLabels" :key="item.key" class="donut-percent-label" :class="item.key" :style="item.style" :title="item.label + ': ' + item.percent + '%'">{{ item.percent }}%</span>
            </div>
          </div>
          <div class="attendance-summary-grid">
            <div v-for="item in attendanceStatusSummary" :key="item.key" class="attendance-summary-item" :class="item.key">
              <i class="legend-dot" :class="item.key" />
              <span>{{ item.label }}</span>
              <strong>{{ item.value }} ngày</strong>
            </div>
            <button type="button" class="attendance-extra-item late" @click="openAttendanceEvents('late')">
              <i class="el-icon-time" /> <span>Đi muộn</span>
              <strong>{{ attendanceSummary.late }} ngày</strong>
              <small>Nhấn để xem chi tiết</small>
            </button>
            <button type="button" class="attendance-extra-item early" @click="openAttendanceEvents('early')">
              <i class="el-icon-right" /> <span>Về sớm</span>
              <strong>{{ attendanceSummary.early }} ngày</strong>
              <small>Nhấn để xem chi tiết</small>
            </button>
          </div>
        </div>
        <p v-if="activeScope !== 'DAY'" class="attendance-calendar-note">{{ attendanceData.elapsedDays }} ngày đã qua · {{ attendanceData.unknownScheduleDays }} ngày thiếu lịch, không tính vào tỷ lệ.</p>

        <div v-if="activeScope !== 'DAY' && attendanceDays.length" class="attendance-timeline">
          <div class="timeline-heading"><strong>Trạng thái từng ngày</strong><span>Chọn ngày để xem chi tiết</span></div>
          <div class="timeline-scroll">
            <button v-for="(day, index) in attendanceDays" :key="index" type="button" :class="['timeline-day', day.status, { selected: selectedAttendanceDayIndex === index }]" @click="selectedAttendanceDayIndex = index">
              <span>{{ day.label }}</span>
              <i class="legend-dot" :class="day.status" />
              <small>{{ day.shortLabel }}</small>
            </button>
          </div>
        </div>

        <div v-if="selectedAttendanceDay" class="attendance-detail">
          <div class="attendance-detail-heading">
            <div><strong>Chi tiết {{ selectedAttendanceDay.label }}</strong><span>{{ activeScope === 'DAY' ? 'Trạng thái trong ngày' : 'Ngày được chọn trong dải thống kê' }}</span></div>
            <span class="attendance-status-badge" :class="selectedAttendanceDay.status">{{ selectedAttendanceDay.statusLabel }}</span>
          </div>
          <div class="attendance-detail-grid">
            <div><span>Giờ đến</span><strong>{{ selectedAttendanceDay.arriveTime }}</strong></div>
            <div><span>Giờ về</span><strong>{{ selectedAttendanceDay.leaveTime }}</strong></div>
            <div><span>Đi muộn</span><strong>{{ selectedAttendanceDay.status === 'present' ? selectedAttendanceDay.lateMinutes + ' phút' : '—' }}</strong></div>
            <div><span>Về sớm</span><strong>{{ selectedAttendanceDay.status === 'present' ? selectedAttendanceDay.earlyMinutes + ' phút' : '—' }}</strong></div>
          </div>
          <div class="attendance-sessions">
            <div><span>Sáng</span><strong>{{ selectedAttendanceDay.sessions.morning }}</strong></div>
            <div><span>Chiều</span><strong>{{ selectedAttendanceDay.sessions.afternoon }}</strong></div>
            <div><span>Tối</span><strong>{{ selectedAttendanceDay.sessions.evening }}</strong></div>
          </div>
          <p>Đi muộn và về sớm là thông tin bổ sung của ngày đi làm; ngày thiếu cấu hình lịch được báo riêng và không tính vào biểu đồ.</p>
        </div>
        <div v-else class="attendance-load-state"><i class="el-icon-date" /><span>Khoảng thời gian đã chọn chưa có ngày đã qua để hiển thị.</span></div>

        <el-dialog :visible.sync="attendanceEventDialogVisible" width="560px" append-to-body custom-class="attendance-event-dialog">
          <div slot="title" class="attendance-dialog-title">
            <span class="attendance-dialog-icon" :class="attendanceEventType"><i :class="attendanceEventType === 'late' ? 'el-icon-time' : 'el-icon-right'" /></span>
            <div><strong>{{ attendanceEventTitle }}</strong><small>{{ periodLabel }} · {{ currentEmployee.name }}</small></div>
          </div>
          <div class="attendance-event-summary">
            <div><small>Số ngày phát sinh</small><strong>{{ attendanceEventRows.length }} <span>ngày</span></strong></div>
            <div :class="attendanceEventType"><small>{{ attendanceEventType === 'late' ? 'Tổng thời gian đi muộn' : 'Tổng thời gian về sớm' }}</small><strong>{{ attendanceEventTotalMinutes }} <span>phút</span></strong></div>
          </div>
          <div v-if="attendanceEventRows.length" class="attendance-event-list">
            <div v-for="(row, index) in attendanceEventRows" :key="row.label" class="attendance-event-row" :class="attendanceEventType">
              <span class="attendance-event-index">{{ String(index + 1).padStart(2, '0') }}</span>
              <div class="attendance-event-date"><strong>{{ row.label }}</strong><small>{{ attendanceEventType === 'late' ? 'Đi muộn' : 'Về sớm' }}</small></div>
              <div class="attendance-event-time"><small>{{ attendanceEventType === 'late' ? 'Giờ đến' : 'Giờ về' }}</small><strong>{{ attendanceEventType === 'late' ? row.arriveTime : row.leaveTime }}</strong></div>
              <span class="attendance-event-minutes">{{ row.minutes }} phút</span>
            </div>
          </div>
          <div v-else class="attendance-event-empty"><span><i class="el-icon-check" /></span><strong>Không có ngày phát sinh</strong><small>Nhân viên không {{ attendanceEventType === 'late' ? 'đi muộn' : 'về sớm' }} trong {{ periodLabel.toLowerCase() }}.</small></div>
        </el-dialog>
        </template>
      </div>

      <div v-else-if="selectedSource === 'TIMETABLE' && (loadingTimetable || timetableError || !timetableData)" class="attendance-load-state" v-loading="loadingTimetable">
        <i :class="timetableError ? 'el-icon-warning-outline' : 'el-icon-loading'" />
        <span>{{ timetableError || 'Đang tải KPI thời khóa biểu...' }}</span>
      </div>

      <div v-else-if="selectedSource === 'MENU' && (loadingMenuKpi || menuKpiError || !menuKpiData)" class="attendance-load-state" v-loading="loadingMenuKpi">
        <i :class="menuKpiError ? 'el-icon-warning-outline' : 'el-icon-loading'" />
        <span>{{ menuKpiError || 'Đang tải KPI thực đơn...' }}</span>
      </div>
      <div v-else-if="selectedSource === 'ALBUM' && (loadingAlbumKpi || albumKpiError || !albumKpiData)" class="attendance-load-state" v-loading="loadingAlbumKpi">
        <i :class="albumKpiError ? 'el-icon-warning-outline' : 'el-icon-loading'" />
        <span>{{ albumKpiError || (currentEmployee.id ? 'Đang tải KPI album...' : 'Chọn nhân viên để xem KPI album.') }}</span>
      </div>

      <div v-else-if="selectedSource === 'STUDENT_ATTENDANCE'" class="ratio-class-panel" v-loading="loadingStudentAttendance">
        <div class="chart-caption">
          <strong>Học sinh đã được điểm danh theo lớp</strong>
          <span>Lớp hiện phụ trách · Sĩ số và trạng thái lấy theo ngày {{ periodLabel }}</span>
        </div>
        <div v-if="studentAttendanceError" class="attendance-load-state error-state">
          <i class="el-icon-warning-outline" /><span>{{ studentAttendanceError }}</span>
        </div>
        <div v-else-if="loadingStudentAttendance" class="attendance-load-state">
          <i class="el-icon-loading" /><span>Đang tải số liệu điểm danh theo lớp...</span>
        </div>
        <div v-else-if="studentAttendanceData && !classMetrics.length" class="attendance-load-state">
          <i class="el-icon-school" /><span>Nhân viên hiện chưa được phân công lớp nào.</span>
        </div>
        <div v-else-if="studentAttendanceData" class="ratio-class-scroll">
          <div v-for="metric in classMetrics" :key="metric.id" class="ratio-class-card">
            <template v-if="metric.dataStatus === 'NO_STUDENTS'">
              <div class="ratio-class-empty"><i class="el-icon-user" /><strong>{{ metric.name }}</strong><span>Không có học sinh trong lớp vào ngày này</span></div>
            </template>
            <template v-else>
              <div class="donut class-donut" :style="ratioClassPieStyle(metric)">
                <div class="donut-hole class-donut-hole">
                  <strong>{{ metric.completionRate }}%</strong>
                  <small>đã điểm danh</small>
                </div>
              </div>
              <strong class="ratio-class-name">{{ metric.name }}</strong>
              <span class="ratio-class-count">{{ metric.value }} / {{ metric.total }} học sinh đã được điểm danh</span>
              <span class="ratio-class-unmarked">Chưa điểm danh: {{ metric.unmarked }} học sinh</span>
            </template>
          </div>
        </div>
        <div v-else class="attendance-load-state"><i class="el-icon-info" /><span>Chọn nhân viên để xem số liệu điểm danh.</span></div>
        <p v-if="studentAttendanceData && classMetrics.length" class="class-note">Tiến độ ghi nhận điểm danh theo lớp; không phụ thuộc nhân viên nào thực hiện.</p>
      </div>

      <div v-else-if="isRatioSource && classMetrics.length" class="ratio-class-panel">
        <div class="chart-caption">
          <strong>{{ selectedSource === 'STUDENT_ATTENDANCE' ? 'Học sinh đã được điểm danh theo lớp' : classRatioTitle }}</strong>
          <span>Mỗi biểu đồ là một lớp nhân viên phụ trách</span>
        </div>
        <div class="ratio-class-scroll">
          <div v-for="metric in classMetrics" :key="metric.id" class="ratio-class-card">
            <div class="donut class-donut" :style="ratioClassPieStyle(metric)">
              <div class="donut-hole class-donut-hole">
                <strong>{{ metric.completionRate === null || metric.completionRate === undefined
                  ? (metric.total ? `${(metric.value / metric.total * 100).toFixed(1)}%` : '—')
                  : `${Number(metric.completionRate).toFixed(1)}%` }}</strong>
                <small>{{ selectedSource === 'STUDENT_ATTENDANCE' ? 'đã điểm danh' : 'đã nhận xét' }}</small>
              </div>
            </div>
            <strong class="ratio-class-name">{{ metric.name }}</strong>
            <span class="ratio-class-count">{{ metric.value }} / {{ metric.total }} học sinh {{ selectedSource === 'STUDENT_ATTENDANCE' ? 'đã được điểm danh' : 'được nhận xét' }}</span>
            <span v-if="selectedSource === 'EVALUATION' && metric.total === 0" class="ratio-class-unmarked">
              {{ metric.rosterTotal ? `Sĩ số ${metric.rosterTotal}; không có học sinh đủ điều kiện trong mẫu số.` : 'Lớp không có học sinh trong kỳ.' }}
            </span>
          </div>
        </div>
        <p class="class-note">{{ selectedSource === 'STUDENT_ATTENDANCE' ? 'Tình trạng điểm danh của lớp, không phụ thuộc người thực hiện.' : 'Chỉ tính học sinh được nhân viên đang chọn nhận xét.' }}</p>
      </div>

      <div v-else-if="classMetrics.length" class="class-chart-layout">
        <div class="class-chart-main">
          <div class="chart-caption">
            <strong>{{ chartTitle }}</strong>
            <span>{{ chartSubtitle }}</span>
          </div>
          <div v-if="chartType === 'BAR'" class="class-bars">
            <div v-for="metric in classMetrics" :key="metric.id" :class="['class-bar-item', { 'read-only': selectedSource === 'ALBUM' }]" @click="selectedSource !== 'ALBUM' && (selectedClassId = metric.id)">
              <strong>{{ metric.value }}</strong>
              <div class="bar-track">
                <div class="bar-value" :style="{ height: barHeight(metric) + '%', backgroundColor: currentSource.color }" />
              </div>
              <small>{{ metric.name }}</small>
            </div>
          </div>
          <div v-else class="pie-area">
            <div class="pie-visual">
              <div class="donut" :style="classPieStyle">
                <div class="donut-hole"><strong>{{ pieCenterValue }}</strong><small>{{ pieCenterLabel }}</small></div>
              </div>
              <template v-if="['TIMETABLE', 'MENU', 'ALBUM'].includes(selectedSource) && classMetrics.length <= 4">
                <span v-for="(metric, index) in classMetrics" :key="metric.id" class="pie-slice-label" :style="pieSliceLabelStyle(index)">
                  <strong>{{ metric.value }}</strong> {{ countUnit }} · <strong>{{ pieShare(metric) }}%</strong>
                </span>
              </template>
            </div>
            <p class="pie-caption">Phân bổ số {{ countUnit }} giữa các lớp</p>
          </div>
          <div v-if="selectedSource === 'MENU'" class="menu-kpi-total">
            <span>Tổng hồ sơ thực đơn trong {{ periodLabel.toLowerCase() }}</span>
            <strong>{{ menuKpiData ? menuKpiData.totalPosts : 0 }} lượt đăng</strong>
          </div>
          <div v-if="selectedSource === 'ALBUM' && albumKpiData" class="menu-kpi-total album-kpi-total">
            <span>Album lớp: {{ albumKpiData.totalClassPosts }} lượt · {{ albumKpiData.uniqueClassAlbums }} album duy nhất · {{ albumResponsePeriodLabel }}</span>
            <strong>Toàn trường: {{ albumKpiData.schoolAlbumCount }} · Đã duyệt {{ albumKpiData.approvedSchoolAlbumCount }} · Chờ duyệt {{ albumKpiData.pendingSchoolAlbumCount }}</strong>
          </div>
        </div>
        <div class="class-list">
          <h4>Các lớp phụ trách <span>{{ classMetrics.length }} lớp</span></h4>
          <div v-for="(metric, index) in classMetrics" :key="metric.id" :class="['class-row', { active: selectedSource !== 'ALBUM' && selectedClassMetric.id === metric.id, 'read-only': selectedSource === 'ALBUM' }]" @click="selectedSource !== 'ALBUM' && (selectedClassId = metric.id)">
            <span class="class-row-name"><i class="class-color-dot" :style="{ backgroundColor: chartColors[index % chartColors.length] }" /> {{ metric.name }}</span>
            <span class="class-row-values"><strong>{{ metric.value }} {{ countUnit }}<span v-if="selectedSource === 'TIMETABLE'"> · {{ metric.percent }}%</span><span v-else-if="selectedSource === 'MENU' || selectedSource === 'ALBUM' || chartType === 'PIE'"> · {{ pieShare(metric) }}%</span></strong><small v-if="selectedSource === 'ALBUM'">Đã duyệt {{ metric.approvedAlbumCount }} · Chờ duyệt {{ metric.pendingAlbumCount }}</small></span>
          </div>
          <p class="class-note">{{ selectedSource === 'TIMETABLE' ? 'Đếm kế hoạch có nội dung theo lớp-tuần, tính theo tài khoản tạo ban đầu.' : selectedSource === 'MENU' ? 'Theo ngày tạo hồ sơ; một bản ghi thực đơn ngày hoặc một hồ sơ tệp tuần là một lượt. Sửa hồ sơ cũ không tạo lượt mới.' : selectedSource === 'ALBUM' ? 'Ghi nhận album V2 theo tài khoản tạo ban đầu; album nhiều lớp tính một lượt ở mỗi lớp. Lớp hiển thị theo phân công hiện tại (CURRENT_ASSIGNMENT); album toàn trường được thống kê riêng.' : 'Chỉ đếm nội dung do nhân viên đã chọn đăng cho lớp.' }}</p>
          <p v-if="selectedSource === 'MENU' && menuKpiData && menuKpiData.ambiguousDailySlots > 0" class="class-note data-warning">
            Có {{ menuKpiData.ambiguousDailySlots }} ngày có bản ghi trùng với người tạo khác nhau; các ngày này đã bị loại khỏi số lượt thực đơn ngày.
          </p>
        </div>
      </div>
      <div v-else class="empty-classes">
        <i class="el-icon-school" />
        <template v-if="selectedSource === 'EVALUATION'">
          <strong v-if="loadingEvaluation">Đang tải KPI nhận xét...</strong>
          <strong v-else-if="evaluationError">{{ evaluationError }}</strong>
          <strong v-else-if="evaluationData">Không có lớp được phân công hoặc không có dữ liệu trong kỳ.</strong>
          <strong v-else>Chọn nhân viên để xem KPI nhận xét theo lớp.</strong>
        </template>
        <template v-else-if="selectedSource === 'ALBUM'">
          <strong v-if="albumKpiData">Chưa có album lớp trong kỳ đã chọn.</strong>
          <strong v-else>Chọn nhân viên để xem KPI album.</strong>
          <span v-if="albumKpiData">Lớp phụ trách hiện tại: {{ albumKpiData.classes.length }} · Album toàn trường: {{ albumKpiData.schoolAlbumCount }} (đã duyệt {{ albumKpiData.approvedSchoolAlbumCount }}, chờ duyệt {{ albumKpiData.pendingSchoolAlbumCount }}).</span>
          <span v-else>Chọn nhân viên để tải dữ liệu Album V2.</span>
        </template>
        <template v-else>
          <strong>{{ selectedSource === 'TIMETABLE' ? 'Nhân viên chưa có lớp được phân công hoặc kế hoạch trong kỳ.' : selectedSource === 'MENU' ? 'Nhân viên hiện chưa được phân công lớp nào.' : 'Nhân viên chưa có lớp phụ trách trong kỳ thống kê.' }}</strong>
          <span>{{ selectedSource === 'TIMETABLE' ? 'Thử chọn nhân viên hoặc khoảng thời gian khác.' : selectedSource === 'MENU' ? 'Thực đơn được phân bổ theo lớp đang phụ trách hiện tại.' : 'Chọn nhân viên khác để xem biểu đồ theo lớp.' }}</span>
        </template>
      </div>
    </div>
  </section>
</template>

<script>
import moment from 'moment'
import DepartmentDataService from '@/services/EmployeeService/DepartmentDataService'
import EmployeeKpiFilterService from '@/services/dashboard_v3/EmployeeKpiFilterService'
import EmployeeAttendanceStatisticsService from '@/services/dashboard_v3/EmployeeAttendanceStatisticsService'
import EmployeeStudentAttendanceKpiService from '@/services/dashboard_v3/EmployeeStudentAttendanceKpiService'
import EmployeeScheduleKpiService from '@/services/dashboard_v3/EmployeeScheduleKpiService'
import EmployeeMenuKpiService from '@/services/dashboard_v3/EmployeeMenuKpiService'
import EmployeeEvaluationKpiService from '@/services/dashboard_v3/EmployeeEvaluationKpiService'
import EmployeeAlbumKpiService from '@/services/dashboard_v3/EmployeeAlbumKpiService'

export default {
  name: 'PersonnelKpiV3',
  props: {
    autoLoad: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      hasLoadedData: false,
      isLoadingData: false,
      selectedSource: 'ATTENDANCE',
      evaluationType: 'DAILY',
      selectedEvaluationDate: moment().format('YYYY-MM-DD'),
      selectedEvaluationWeek: moment().toDate(),
      selectedEvaluationMonth: moment().format('YYYY-MM'),
      selectedEvaluationYear: moment().format('YYYY'),
      selectedEvaluationPeriodNumber: 1,
      selectedEvaluationAnchorDate: moment().format('YYYY-MM-DD'),
      evaluationData: null,
      evaluationError: '',
      loadingEvaluation: false,
      chartType: 'BAR',
      selectedClassId: 'MAM_1',
      selectedAttendanceDayIndex: 0,
      attendanceEventDialogVisible: false,
      attendanceEventType: 'late',
      attendanceData: null,
      attendanceError: '',
      loadingAttendance: false,
      studentAttendanceData: null,
      studentAttendanceError: '',
      loadingStudentAttendance: false,
      timetableData: null,
      timetableError: '',
      loadingTimetable: false,
      menuKpiData: null,
      menuKpiError: '',
      loadingMenuKpi: false,
      albumKpiData: null,
      albumKpiError: '',
      loadingAlbumKpi: false,
      loadingEmployees: false,
      employeesError: '',
      employeeSearchQuery: '',
      scopeFilters: {
        ATTENDANCE: { scope: 'DAY', dayDate: moment().toDate(), weekDate: moment().toDate(), monthDate: moment().toDate(), dateRange: [moment().subtract(6, 'days').toDate(), moment().toDate()] },
        STUDENT_ATTENDANCE: { scope: 'DAY', dayDate: moment().toDate(), weekDate: moment().toDate(), monthDate: moment().toDate(), dateRange: [moment().subtract(6, 'days').toDate(), moment().toDate()] },
        EVALUATION: { scope: 'DAY', dayDate: moment().toDate(), weekDate: moment().toDate(), monthDate: moment().toDate(), dateRange: [moment().subtract(6, 'days').toDate(), moment().toDate()] },
        TIMETABLE: { scope: 'DAY', dayDate: moment().toDate(), weekDate: moment().toDate(), monthDate: moment().toDate(), dateRange: [moment().subtract(6, 'days').toDate(), moment().toDate()] },
        MENU: { scope: 'DAY', dayDate: moment().toDate(), weekDate: moment().toDate(), monthDate: moment().toDate(), dateRange: [moment().subtract(6, 'days').toDate(), moment().toDate()] },
        ALBUM: { scope: 'DAY', dayDate: moment().toDate(), weekDate: moment().toDate(), monthDate: moment().toDate(), dateRange: [moment().subtract(6, 'days').toDate(), moment().toDate()] }
      },
      weekPickerOptions: { firstDayOfWeek: 1 },
      chartColors: ['#356af3', '#16a085', '#a261e8', '#ed8c3a', '#e25b8d', '#64748b'],
      selectedDepartment: 'ALL',
      selectedEmployee: null,
      departments: [{ id: 'ALL', departmentName: 'Tất cả phòng ban' }],
      employees: [],
      albumEligibleEmployeeIds: [],
      scopes: [
        { value: 'DAY', label: 'Ngày' },
        { value: 'WEEK', label: 'Tuần' },
        { value: 'MONTH', label: 'Tháng' },
        { value: 'RANGE', label: 'Khoảng ngày' }
      ],
      evaluationTypes: [
        { value: 'DAILY', label: 'Nhận xét ngày' },
        { value: 'WEEKLY', label: 'Nhận xét tuần' },
        { value: 'MONTHLY', label: 'Nhận xét tháng' },
        { value: 'PERIODIC', label: 'Định kì' }
      ],
      sources: [
        { key: 'ATTENDANCE', title: 'Chấm công', description: 'Nhân sự đi làm', icon: 'el-icon-s-check', color: '#168c78', background: '#e5f8f3' },
        { key: 'STUDENT_ATTENDANCE', title: 'Điểm danh', description: 'Điểm danh học sinh', icon: 'el-icon-user', color: '#356af3', background: '#eaf0ff' },
        { key: 'EVALUATION', title: 'Nhận xét', description: 'Nhận xét, đánh giá', icon: 'el-icon-chat-dot-round', color: '#8656e8', background: '#f2ecff' },
        { key: 'TIMETABLE', title: 'Thời khóa biểu', description: 'Kế hoạch học tập', icon: 'el-icon-date', color: '#2386ca', background: '#e9f5ff' },
        { key: 'MENU', title: 'Thực đơn', description: 'Kế hoạch bữa ăn', icon: 'el-icon-dish', color: '#e17d27', background: '#fff2e5' },
        { key: 'ALBUM', title: 'Album ảnh', description: 'Hoạt động đăng ảnh', icon: 'el-icon-picture-outline', color: '#d75591', background: '#fff0f7' }
      ]
    }
  },
  computed: {
    attendanceQueryKey() {
      if (this.selectedSource !== 'ATTENDANCE' || !this.currentEmployee.id) return ''
      const [startDate, endDate] = this.attendanceDateRange()
      return `${this.currentEmployee.id}|${startDate}|${endDate}`
    },
    studentAttendanceQueryKey() {
      if (this.selectedSource !== 'STUDENT_ATTENDANCE' || !this.currentEmployee.id || !this.selectedDayDate) return ''
      return `${this.currentEmployee.id}|${moment(this.selectedDayDate).format('YYYY-MM-DD')}`
    },
    evaluationQueryKey() {
      if (this.selectedSource !== 'EVALUATION' || !this.currentEmployee.id) return ''
      if (this.evaluationType === 'DAILY') {
        return `${this.currentEmployee.id}|DAILY|${this.selectedEvaluationDate}`
      }
      if (this.evaluationType === 'WEEKLY') {
        return `${this.currentEmployee.id}|WEEKLY|${moment(this.selectedEvaluationWeek).startOf('isoWeek').format('YYYY-MM-DD')}`
      }
      if (this.evaluationType === 'MONTHLY') {
        return `${this.currentEmployee.id}|MONTHLY|${this.selectedEvaluationMonth}`
      }
      return `${this.currentEmployee.id}|PERIODIC|${this.selectedEvaluationYear}|${this.selectedEvaluationPeriodNumber}|${this.selectedEvaluationAnchorDate}`
    },
    evaluationDatePickerOptions() {
      return { disabledDate: date => moment(date).isAfter(moment(), 'day') }
    },
    evaluationYearPickerOptions() {
      return { disabledDate: date => moment(date).isAfter(moment(), 'year') }
    },
    evaluationAnchorPickerOptions() {
      return { disabledDate: date => moment(date).format('YYYY') !== String(this.selectedEvaluationYear)
        || moment(date).isAfter(moment(), 'day') }
    },
    timetableQueryKey() {
      if (this.selectedSource !== 'TIMETABLE' || !this.currentEmployee.id) return ''
      const [startDate, endDate] = this.attendanceDateRange()
      return `${this.currentEmployee.id}|${startDate}|${endDate}`
    },
    menuKpiQueryKey() {
      if (this.selectedSource !== 'MENU' || !this.currentEmployee.id) return ''
      const date = this.menuKpiAnchorDate()
      return `${this.currentEmployee.id}|${this.activeScope}|${date}`
    },
    albumKpiQueryKey() {
      if (this.selectedSource !== 'ALBUM' || !this.currentEmployee.id) return ''
      const [startDate, endDate] = this.attendanceDateRange()
      return `${this.currentEmployee.id}|${this.activeScope}|${startDate}|${endDate}`
    },
    studentAttendanceDatePickerOptions() {
      return { disabledDate: date => moment(date).isAfter(moment(), 'day') }
    },
    albumWeekPickerOptions() {
      return { firstDayOfWeek: 1, disabledDate: date => moment(date).startOf('isoWeek').isAfter(moment(), 'day') }
    },
    albumMonthPickerOptions() {
      return { disabledDate: date => moment(date).isAfter(moment(), 'month') }
    },
    albumRangePickerOptions() {
      return { disabledDate: date => moment(date).isAfter(moment(), 'day') }
    },
    availableScopes() {
      if (this.selectedSource === 'STUDENT_ATTENDANCE') return this.scopes.filter(scope => scope.value === 'DAY')
      if (this.selectedSource === 'MENU') return this.scopes.filter(scope => ['DAY', 'WEEK', 'MONTH'].includes(scope.value))
      return this.scopes
    },
    activeFilter() {
      return this.scopeFilters[this.selectedSource]
    },
    activeScope: {
      get() {
        return this.activeFilter.scope
      },
      set(value) {
        this.$set(this.activeFilter, 'scope', value)
      }
    },
    selectedDayDate: {
      get() {
        return this.activeFilter.dayDate
      },
      set(value) {
        this.$set(this.activeFilter, 'dayDate', value)
      }
    },
    selectedWeekDate: {
      get() {
        return this.activeFilter.weekDate
      },
      set(value) {
        this.$set(this.activeFilter, 'weekDate', value)
      }
    },
    selectedMonthDate: {
      get() {
        return this.activeFilter.monthDate
      },
      set(value) {
        this.$set(this.activeFilter, 'monthDate', value)
      }
    },
    selectedDateRange: {
      get() {
        return this.activeFilter.dateRange
      },
      set(value) {
        this.$set(this.activeFilter, 'dateRange', value)
      }
    },
    periodLabel() {
      if (this.activeScope === 'DAY') return moment(this.selectedDayDate).format('DD/MM/YYYY')
      if (this.activeScope === 'WEEK') {
        const start = moment(this.selectedWeekDate).startOf('isoWeek')
        const end = start.clone().add(6, 'days')
        if (this.selectedSource === 'ALBUM' && end.isAfter(moment(), 'day')) end.set({ year: moment().year(), month: moment().month(), date: moment().date() })
        return `Tuần ${start.isoWeek()} (${start.format('DD/MM')}–${end.format('DD/MM/YYYY')})`
      }
      if (this.activeScope === 'RANGE') {
        const [start, end] = this.selectedDateRange || []
        return start && end ? `${moment(start).format('DD/MM/YYYY')} – ${moment(end).format('DD/MM/YYYY')}` : 'Chọn khoảng ngày'
      }
      return `Tháng ${moment(this.selectedMonthDate).format('MM/YYYY')}`
    },
    isRatioSource() {
      return ['STUDENT_ATTENDANCE', 'EVALUATION'].includes(this.selectedSource)
    },
    classMetrics() {
      if (this.selectedSource === 'STUDENT_ATTENDANCE') {
        return (this.studentAttendanceData && this.studentAttendanceData.classes || []).map(item => ({
          id: item.classId,
          name: item.className,
          total: Number(item.totalStudents || 0),
          value: Number(item.markedStudents || 0),
          unmarked: Number(item.unmarkedStudents || 0),
          completionRate: item.completionRate,
          dataStatus: item.dataStatus
        }))
      }
      if (this.selectedSource === 'EVALUATION') {
        return (this.evaluationData && this.evaluationData.classes || []).map(item => ({
          id: item.classId,
          name: item.className,
          total: Number(item.eligibleStudents || 0),
          rosterTotal: Number(item.totalStudents || 0),
          value: Number(item.reviewedStudents || 0),
          completionRate: item.rate,
          dataStatus: item.totalStudents ? 'HAS_STUDENTS' : 'NO_STUDENTS'
        }))
      }
      if (this.selectedSource === 'TIMETABLE') {
        return (this.timetableData && this.timetableData.classes || []).map(item => ({
          id: item.classId,
          name: item.className,
          total: Number(item.count || 0),
          value: Number(item.count || 0),
          percent: Number(item.percent || 0)
        }))
      }
      if (this.selectedSource === 'MENU') {
        return (this.menuKpiData && this.menuKpiData.classes || []).map(item => ({
          id: item.classId,
          name: item.className,
          total: Number(item.postCount || 0),
          value: Number(item.postCount || 0),
          sharePercent: Number(item.sharePercent || 0),
          dailyMenuCount: Number(item.dailyMenuCount || 0),
          weeklyFileCount: Number(item.weeklyFileCount || 0)
        }))
      }
      if (this.selectedSource === 'ALBUM') {
        return (this.albumKpiData && this.albumKpiData.classes || []).map(item => ({
          id: item.classId,
          name: item.className,
          total: Number(item.albumCount || 0),
          value: Number(item.albumCount || 0),
          percent: Number(item.percentage || 0),
          approvedAlbumCount: Number(item.approvedAlbumCount || 0),
          pendingAlbumCount: Number(item.pendingAlbumCount || 0)
        }))
      }
      return []
    },
    selectedClassMetric() {
      return this.classMetrics.find(item => item.id === this.selectedClassId) || this.classMetrics[0] || { id: '', name: '', value: 0, total: 0 }
    },
    chartMax() {
      return Math.max(1, ...this.classMetrics.map(item => item.value))
    },
    countUnit() {
      if (this.selectedSource === 'TIMETABLE') return 'kế hoạch lớp-tuần'
      return this.selectedSource === 'ALBUM' ? 'album' : 'lượt đăng'
    },
    chartTitle() {
      if (this.selectedSource === 'ALBUM') return 'Số album ảnh nhân viên đã đăng theo lớp'
      if (this.selectedSource === 'TIMETABLE') return 'Số kế hoạch lớp-tuần nhân viên đã tạo theo lớp'
      return 'Số lượt ' + this.currentSource.title.toLowerCase() + ' nhân viên đã đăng theo lớp'
    },
    chartSubtitle() {
      return this.chartType === 'PIE'
        ? `Biểu đồ thể hiện số ${this.countUnit} và tỷ trọng của từng lớp`
        : `Mỗi cột thể hiện số ${this.countUnit} của nhân viên ở một lớp`
    },
    classPieStyle() {
      const total = this.classMetrics.reduce((sum, item) => sum + item.value, 0)
      if (!total) return { background: '#e5ebf4' }
      let start = 0
      const segments = this.classMetrics.map((item, index) => {
        const end = start + item.value / total * 100
        const segment = `${this.chartColors[index % this.chartColors.length]} ${start}% ${end}%`
        start = end
        return segment
      })
      return { background: `conic-gradient(${segments.join(', ')})` }
    },
    pieCenterValue() {
      return this.classMetrics.reduce((sum, item) => sum + item.value, 0)
    },
    pieCenterLabel() {
      return this.countUnit
    },
    attendanceDays() {
      const statusLabels = {
        PRESENT: ['present', 'Đi làm'],
        ABSENT_PERMITTED: ['permitted', 'Nghỉ có phép'],
        ABSENT_UNPERMITTED: ['unpermitted', 'Nghỉ không phép'],
        UNMARKED: ['unmarked', 'Chưa chấm công'],
        OFF: ['off', 'Ngày nghỉ'],
        UNKNOWN_SCHEDULE: ['unknown', 'Chưa có lịch']
      }
      const sessionLabels = {
        PRESENT: 'Đi làm', ABSENT_PERMITTED: 'Nghỉ phép',
        ABSENT_UNPERMITTED: 'Nghỉ không phép', UNMARKED: 'Chưa chấm',
        OFF: '—', UNKNOWN_SCHEDULE: 'Chưa có lịch'
      }
      return (this.attendanceData && this.attendanceData.days || []).map(day => {
        const [status, statusLabel] = statusLabels[day.status] || ['unknown', 'Chưa xác định']
        const mapSession = session => sessionLabels[session && session.status] || '—'
        return {
          ...day,
          label: day.dayLabel,
          shortLabel: statusLabel,
          status,
          statusLabel,
          arriveTime: day.arriveTime || '—',
          leaveTime: day.leaveTime || '—',
          sessions: {
            morning: mapSession(day.morning),
            afternoon: mapSession(day.afternoon),
            evening: mapSession(day.evening)
          }
        }
      })
    },
    attendanceSummary() {
      const data = this.attendanceData || {}
      return {
        present: data.presentDays || 0,
        permitted: data.absentPermittedDays || 0,
        unpermitted: data.absentUnpermittedDays || 0,
        unmarked: data.unmarkedDays || 0,
        off: data.offDays || 0,
        late: data.lateDays || 0,
        early: data.earlyDays || 0,
        lateMinutes: data.lateMinutes || 0,
        earlyMinutes: data.earlyMinutes || 0
      }
    },
    attendanceStatusSummary() {
      const data = this.attendanceData || {}
      return [
        { key: 'present', label: 'Đi làm', value: this.attendanceSummary.present, percent: Number(data.presentPercent || 0) },
        { key: 'permitted', label: 'Nghỉ có phép', value: this.attendanceSummary.permitted, percent: Number(data.absentPermittedPercent || 0) },
        { key: 'unpermitted', label: 'Nghỉ không phép', value: this.attendanceSummary.unpermitted, percent: Number(data.absentUnpermittedPercent || 0) },
        { key: 'unmarked', label: 'Chưa chấm công', value: this.attendanceSummary.unmarked, percent: Number(data.unmarkedPercent || 0) },
        { key: 'off', label: 'Ngày nghỉ', value: this.attendanceSummary.off, percent: Number(data.offPercent || 0) }
      ]
    },
    attendanceRecordedDayCount() {
      return this.attendanceData ? this.attendanceData.classifiedDays : 0
    },
    attendancePieStyle() {
      const total = this.attendanceRecordedDayCount
      if (!total) return { background: '#e5ebf4' }
      const colors = { present: '#20a779', permitted: '#f59e0b', unpermitted: '#ef5b68', unmarked: '#93a5bc', off: '#cbd5e1' }
      let start = 0
      const segments = this.attendanceStatusSummary.map(item => {
        const end = start + item.percent
        const segment = `${colors[item.key]} ${start}% ${end}%`
        start = end
        return segment
      })
      return { background: `conic-gradient(${segments.join(', ')})` }
    },
    attendancePieLabels() {
      const total = this.attendanceRecordedDayCount
      let start = 0
      return this.attendanceStatusSummary.reduce((labels, item) => {
        const percentValue = total ? item.percent : 0
        const end = start + percentValue
        if (percentValue >= 8) {
          const angle = (start + percentValue / 2) / 100 * Math.PI * 2 - Math.PI / 2
          labels.push({
            ...item,
            percent: Math.round(percentValue),
            style: {
              left: `${50 + Math.cos(angle) * 42}%`,
              top: `${50 + Math.sin(angle) * 42}%`
            }
          })
        }
        start = end
        return labels
      }, [])
    },
    attendanceEventTitle() {
      return this.attendanceEventType === 'late' ? 'Chi tiết ngày đi muộn' : 'Chi tiết ngày về sớm'
    },
    attendanceEventRows() {
      const minutesKey = this.attendanceEventType === 'late' ? 'lateMinutes' : 'earlyMinutes'
      const timeKey = this.attendanceEventType === 'late' ? 'arriveTime' : 'leaveTime'
      return this.attendanceDays
        .filter(day => day.status === 'present' && day[minutesKey] > 0)
        .map(day => ({ label: day.label, minutes: day[minutesKey], arriveTime: day.arriveTime, leaveTime: day.leaveTime, [timeKey]: day[timeKey] }))
    },
    attendanceEventTotalMinutes() {
      return this.attendanceEventRows.reduce((total, row) => total + row.minutes, 0)
    },
    filteredEmployees() {
      const sourceEmployees = this.selectedSource === 'ALBUM'
        ? this.employees.filter(employee => this.albumEligibleEmployeeIds.includes(employee.id))
        : this.employees
      return this.selectedDepartment === 'ALL'
        ? sourceEmployees
        : sourceEmployees.filter(employee => employee.departmentIds.includes(Number(this.selectedDepartment)))
    },
    employeeSearchResults() {
      const query = this.normalizeEmployeeSearch(this.employeeSearchQuery)
      if (!query) return this.filteredEmployees
      return this.filteredEmployees.filter(employee => this.normalizeEmployeeSearch(employee.name).includes(query))
    },
    currentEmployee() {
      return this.filteredEmployees.find(employee => employee.id === this.selectedEmployee) || this.filteredEmployees[0] || {
        id: null, name: 'Chưa chọn nhân viên', initials: '--', departmentIds: [], classIds: []
      }
    },
    currentDepartmentName() {
      const departmentIds = this.currentEmployee.departmentIds || []
      if (departmentIds.includes(this.selectedDepartment)) {
        const selectedDepartment = this.departments.find(item => item.id === this.selectedDepartment)
        if (selectedDepartment) return selectedDepartment.departmentName
      }
      const names = this.departments
        .filter(item => departmentIds.includes(item.id))
        .map(item => item.departmentName)
      return names.length ? names.join(', ') : (this.currentEmployee.status || '')
    },
    currentSource() {
      return this.sources.find(source => source.key === this.selectedSource) || this.sources[0]
    },
    currentEvaluationType() {
      return this.evaluationTypes.find(type => type.value === this.evaluationType) || this.evaluationTypes[0]
    },
    classRatioTitle() {
      return `${this.currentEvaluationType.label} theo lớp`
    },
    albumResponsePeriodLabel() {
      if (!this.albumKpiData) return ''
      const start = moment(this.albumKpiData.startDate).format('DD/MM/YYYY')
      const end = moment(this.albumKpiData.endDate).format('DD/MM/YYYY')
      return start === end ? start : `${start} – ${end}`
    },
    selectedAttendanceDay() {
      return this.attendanceDays[this.selectedAttendanceDayIndex] || null
    }
  },
  watch: {
    attendanceQueryKey() {
      this.loadAttendanceKpi()
    },
    studentAttendanceQueryKey() {
      this.loadStudentAttendanceKpi()
    },
    timetableQueryKey() {
      this.loadTimetableKpi()
    },
    evaluationQueryKey() {
      this.loadEvaluationKpi()
    },
    menuKpiQueryKey() {
      this.loadMenuKpi()
    },
    albumKpiQueryKey() {
      this.loadAlbumKpi()
    }
  },
  created() {
    if (this.autoLoad) {
      this.loadData()
    }
  },
  methods: {
    async loadData() {
      if (this.hasLoadedData || this.isLoadingData) return
      this.isLoadingData = true
      try {
        await this.loadEmployeeOptions()
        this.hasLoadedData = true
      } finally {
        this.isLoadingData = false
      }
    },
    openAttendanceEvents(type) {
      this.attendanceEventType = type
      this.attendanceEventDialogVisible = true
    },
    changeScope(scope) {
      this.activeScope = scope
      this.selectedAttendanceDayIndex = 0
    },
    selectSource(source) {
      this.selectedSource = source
      if (source === 'STUDENT_ATTENDANCE') {
        this.$set(this.scopeFilters[source], 'scope', 'DAY')
      }
      if (!this.filteredEmployees.some(employee => employee.id === this.selectedEmployee)) {
        this.selectedEmployee = this.filteredEmployees.length ? this.filteredEmployees[0].id : null
      }
      this.selectedAttendanceDayIndex = 0
    },
    resetAttendanceDay() {
      this.selectedAttendanceDayIndex = 0
    },
    pieShare(metric) {
      if (this.selectedSource === 'MENU') return Number(metric.sharePercent || 0)
      if (this.selectedSource === 'ALBUM') return Number(metric.percent || 0)
      const total = this.classMetrics.reduce((sum, item) => sum + item.value, 0)
      return total ? Math.round(metric.value / total * 100) : 0
    },
    pieSliceLabelStyle(index) {
      const total = this.classMetrics.reduce((sum, item) => sum + item.value, 0)
      if (!total) return { display: 'none' }
      const before = this.classMetrics.slice(0, index).reduce((sum, item) => sum + item.value, 0)
      const middle = (before + this.classMetrics[index].value / 2) / total
      const angle = middle * Math.PI * 2 - Math.PI / 2
      return {
        left: `${50 + Math.cos(angle) * 35}%`,
        top: `${50 + Math.sin(angle) * 40}%`,
        borderColor: this.chartColors[index % this.chartColors.length]
      }
    },
    ratioClassPieStyle(metric) {
      const percent = metric.completionRate === null || metric.completionRate === undefined
        ? (metric.total ? metric.value / metric.total * 100 : 0)
        : Number(metric.completionRate)
      return { background: `conic-gradient(${this.currentSource.color} 0 ${percent}%, #e5ebf4 ${percent}% 100%)` }
    },
    barHeight(metric) {
      return Math.max(4, Math.round(metric.value / this.chartMax * 100))
    },
    onDepartmentChange() {
      this.employeeSearchQuery = ''
      if (!this.filteredEmployees.some(employee => employee.id === this.selectedEmployee)) {
        this.selectedEmployee = this.filteredEmployees.length ? this.filteredEmployees[0].id : null
      }
    },
    filterEmployeeOptions(query) {
      this.employeeSearchQuery = query || ''
    },
    searchEmployeesOnEnter(event) {
      const select = this.$refs.employeeSelect
      const query = event.target && typeof event.target.value === 'string'
        ? event.target.value
        : this.employeeSearchQuery
      if (!query.trim()) return

      event.preventDefault()
      event.stopPropagation()
      this.filterEmployeeOptions(query)
      if (select) {
        select.query = query
        select.isOnComposition = false
        select.previousQuery = query
        select.visible = true
        this.$nextTick(() => select.checkDefaultFirstOption())
      }
    },
    normalizeEmployeeSearch(value) {
      return String(value || '').trim().toLocaleLowerCase('vi')
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/đ/g, 'd')
    },
    onEmployeeChange() {
      this.employeeSearchQuery = ''
      this.selectedAttendanceDayIndex = 0
    },
    attendanceDateRange() {
      if (this.activeScope === 'WEEK') {
        const start = moment(this.selectedWeekDate).startOf('isoWeek')
        return [start.format('YYYY-MM-DD'), start.clone().add(6, 'days').format('YYYY-MM-DD')]
      }
      if (this.activeScope === 'MONTH') {
        const start = moment(this.selectedMonthDate).startOf('month')
        return [start.format('YYYY-MM-DD'), start.clone().endOf('month').format('YYYY-MM-DD')]
      }
      if (this.activeScope === 'RANGE') {
        const range = this.selectedDateRange || []
        if (!range[0] || !range[1]) return ['', '']
        return [moment(range[0]).format('YYYY-MM-DD'), moment(range[1]).format('YYYY-MM-DD')]
      }
      const date = moment(this.selectedDayDate).format('YYYY-MM-DD')
      return [date, date]
    },
    menuKpiAnchorDate() {
      if (this.activeScope === 'WEEK') return moment(this.selectedWeekDate).format('YYYY-MM-DD')
      if (this.activeScope === 'MONTH') return moment(this.selectedMonthDate).startOf('month').format('YYYY-MM-DD')
      return moment(this.selectedDayDate).format('YYYY-MM-DD')
    },
    async loadEmployeeOptions() {
      this.loadingEmployees = true
      this.employeesError = ''
      try {
        const [departmentResponse, allEmployeesResponse, albumEmployeesResponse] = await Promise.all([
          DepartmentDataService.getAllCommon(),
          EmployeeKpiFilterService.searchEmployees(-3, 'ALL'),
          EmployeeKpiFilterService.searchEmployees(-3, 'ALBUM')
        ])
        const departments = departmentResponse.data && departmentResponse.data.data || []
        this.departments = [{ id: 'ALL', departmentName: 'Tất cả phòng ban' }, ...departments]
        const employeeList = allEmployeesResponse.data && allEmployeesResponse.data.data && allEmployeesResponse.data.data.dataList || []
        const albumEmployeeList = albumEmployeesResponse.data && albumEmployeesResponse.data.data && albumEmployeesResponse.data.data.dataList || []
        this.albumEligibleEmployeeIds = albumEmployeeList.map(employee => Number(employee.id))
        this.employees = employeeList.map(employee => {
          const departmentIds = (employee.departmentIds || []).map(Number)
          const words = (employee.employeeName || '').trim().split(/\s+/)
          return {
            id: Number(employee.id),
            name: employee.employeeName,
            initials: words.map(word => word[0]).slice(-2).join('').toUpperCase(),
            departmentIds,
            departmentNames: this.departments.filter(department => departmentIds.includes(Number(department.id))).map(department => department.departmentName),
            classIds: [],
            status: employee.employeeStatus || ''
          }
        }).sort((a, b) => a.name.localeCompare(b.name, 'vi'))
        if (!this.employees.some(employee => employee.id === this.selectedEmployee)) {
          this.selectedEmployee = this.employees.length ? this.employees[0].id : null
        }
      } catch (error) {
        this.employeesError = 'Không tải được danh sách nhân viên.'
        this.$message.error(this.employeesError)
      } finally {
        this.loadingEmployees = false
      }
    },
    async loadAttendanceKpi() {
      const requestId = (this._attendanceRequestId || 0) + 1
      this._attendanceRequestId = requestId
      if (this.selectedSource !== 'ATTENDANCE' || !this.currentEmployee.id) {
        this.attendanceData = null
        this.attendanceError = ''
        this.loadingAttendance = false
        return
      }
      const [startDate, endDate] = this.attendanceDateRange()
      if (!startDate || !endDate) return
      this.loadingAttendance = true
      this.attendanceError = ''
      this.attendanceData = null
      try {
        const response = await EmployeeAttendanceStatisticsService.getKpi(this.currentEmployee.id, startDate, endDate)
        if (requestId !== this._attendanceRequestId) return
        const data = response.data && response.data.data
        if (!data || Number(data.employeeId) !== Number(this.currentEmployee.id) || !Array.isArray(data.days)) {
          throw new Error('Invalid employee attendance KPI response')
        }
        this.attendanceData = data
        this.selectedAttendanceDayIndex = 0
      } catch (error) {
        if (requestId !== this._attendanceRequestId) return
        this.attendanceError = 'Không tải được KPI chấm công cho nhân viên và khoảng ngày đã chọn.'
      } finally {
        if (requestId === this._attendanceRequestId) this.loadingAttendance = false
      }
    },
    async loadStudentAttendanceKpi() {
      const requestId = (this._studentAttendanceRequestId || 0) + 1
      this._studentAttendanceRequestId = requestId
      if (this.selectedSource !== 'STUDENT_ATTENDANCE' || !this.currentEmployee.id || !this.selectedDayDate) {
        this.studentAttendanceData = null
        this.studentAttendanceError = ''
        this.loadingStudentAttendance = false
        return
      }
      const date = moment(this.selectedDayDate).format('YYYY-MM-DD')
      this.studentAttendanceData = null
      this.studentAttendanceError = ''
      this.loadingStudentAttendance = true
      try {
        const response = await EmployeeStudentAttendanceKpiService.getKpi(this.currentEmployee.id, date)
        if (requestId !== this._studentAttendanceRequestId) return
        const data = response.data && response.data.data
        if (!data || Number(data.employeeId) !== Number(this.currentEmployee.id)
            || data.date !== date || !Array.isArray(data.classes)) {
          throw new Error('Invalid employee student attendance KPI response')
        }
        this.studentAttendanceData = data
      } catch (error) {
        if (requestId !== this._studentAttendanceRequestId) return
        this.studentAttendanceError = error.response && error.response.status === 422
          ? 'Thiếu lịch sử học sinh/lớp trong ngày đã chọn; chưa thể xác nhận số liệu.'
          : 'Không tải được số liệu điểm danh học sinh cho nhân viên và ngày đã chọn.'
      } finally {
        if (requestId === this._studentAttendanceRequestId) this.loadingStudentAttendance = false
      }
    },
    async loadTimetableKpi() {
      const requestId = (this._timetableRequestId || 0) + 1
      this._timetableRequestId = requestId
      if (this.selectedSource !== 'TIMETABLE' || !this.currentEmployee.id) {
        this.timetableData = null
        this.timetableError = ''
        this.loadingTimetable = false
        return
      }
      const [startDate, endDate] = this.attendanceDateRange()
      if (!startDate || !endDate) return
      this.loadingTimetable = true
      this.timetableData = null
      this.timetableError = ''
      try {
        const response = await EmployeeScheduleKpiService.getKpi(this.currentEmployee.id, startDate, endDate)
        if (requestId !== this._timetableRequestId) return
        const data = response.data && response.data.data
        if (!data || Number(data.employeeId) !== Number(this.currentEmployee.id) || !Array.isArray(data.classes)) {
          throw new Error('Invalid employee timetable KPI response')
        }
        this.timetableData = data
      } catch (error) {
        if (requestId !== this._timetableRequestId) return
        this.timetableError = 'Không tải được KPI thời khóa biểu cho nhân viên và khoảng ngày đã chọn.'
      } finally {
        if (requestId === this._timetableRequestId) this.loadingTimetable = false
      }
    },
    async loadMenuKpi() {
      const requestId = (this._menuKpiRequestId || 0) + 1
      this._menuKpiRequestId = requestId
      if (this.selectedSource !== 'MENU' || !this.currentEmployee.id) {
        this.menuKpiData = null
        this.menuKpiError = ''
        this.loadingMenuKpi = false
        return
      }
      const mode = this.activeScope
      const date = this.menuKpiAnchorDate()
      this.menuKpiData = null
      this.menuKpiError = ''
      this.loadingMenuKpi = true
      try {
        const response = await EmployeeMenuKpiService.getKpi(this.currentEmployee.id, mode, date)
        if (requestId !== this._menuKpiRequestId) return
        const data = response.data && response.data.data
        if (!data || Number(data.employeeId) !== Number(this.currentEmployee.id)
            || data.mode !== mode || !Array.isArray(data.classes)) {
          throw new Error('Invalid employee menu KPI response')
        }
        this.menuKpiData = data
      } catch (error) {
        if (requestId !== this._menuKpiRequestId) return
        this.menuKpiError = 'Không tải được KPI thực đơn cho nhân viên và kỳ đã chọn.'
      } finally {
        if (requestId === this._menuKpiRequestId) this.loadingMenuKpi = false
      }
    },
    async loadAlbumKpi() {
      const requestId = (this._albumKpiRequestId || 0) + 1
      this._albumKpiRequestId = requestId
      if (this.selectedSource !== 'ALBUM' || !this.currentEmployee.id) {
        this.albumKpiData = null
        this.albumKpiError = ''
        this.loadingAlbumKpi = false
        return
      }
      const [startDate, endDate] = this.attendanceDateRange()
      if (!startDate || !endDate) return
      const params = { employeeId: this.currentEmployee.id, mode: this.activeScope }
      if (this.activeScope === 'RANGE') Object.assign(params, { startDate, endDate })
      else params.date = this.activeScope === 'WEEK' ? moment(this.selectedWeekDate).format('YYYY-MM-DD')
        : this.activeScope === 'MONTH' ? moment(this.selectedMonthDate).format('YYYY-MM-DD') : startDate
      this.loadingAlbumKpi = true
      this.albumKpiError = ''
      this.albumKpiData = null
      try {
        const response = await EmployeeAlbumKpiService.getKpi(params)
        if (requestId !== this._albumKpiRequestId) return
        const data = response.data && response.data.data
        if (!data || Number(data.employeeId) !== Number(this.currentEmployee.id)
            || !Array.isArray(data.classes) || data.mode !== this.activeScope) {
          throw new Error('Invalid employee album KPI response')
        }
        this.albumKpiData = data
      } catch (error) {
        if (requestId !== this._albumKpiRequestId) return
        this.albumKpiError = 'Không tải được KPI album cho nhân viên và kỳ đã chọn.'
      } finally {
        if (requestId === this._albumKpiRequestId) this.loadingAlbumKpi = false
      }
    },
    updateEvaluationAnchor() {
      const yearEnd = moment(`${this.selectedEvaluationYear}-12-31`, 'YYYY-MM-DD')
      this.selectedEvaluationAnchorDate = (yearEnd.isAfter(moment()) ? moment() : yearEnd).format('YYYY-MM-DD')
    },
    async loadEvaluationKpi() {
      const requestId = (this._evaluationRequestId || 0) + 1
      this._evaluationRequestId = requestId
      if (this.selectedSource !== 'EVALUATION' || !this.currentEmployee.id) {
        this.evaluationData = null
        this.evaluationError = ''
        this.loadingEvaluation = false
        return
      }
      const params = { employeeId: this.currentEmployee.id, type: this.evaluationType }
      if (this.evaluationType === 'DAILY') params.date = this.selectedEvaluationDate
      else if (this.evaluationType === 'WEEKLY') {
        params.date = moment(this.selectedEvaluationWeek).startOf('isoWeek').format('YYYY-MM-DD')
      } else if (this.evaluationType === 'MONTHLY') params.month = this.selectedEvaluationMonth
      else {
        params.year = Number(this.selectedEvaluationYear)
        params.periodNumber = Number(this.selectedEvaluationPeriodNumber)
        params.anchorDate = this.selectedEvaluationAnchorDate
      }
      this.loadingEvaluation = true
      this.evaluationData = null
      this.evaluationError = ''
      try {
        const response = await EmployeeEvaluationKpiService.getKpi(params)
        if (requestId !== this._evaluationRequestId) return
        const data = response.data && response.data.data
        if (!data || Number(data.employeeId) !== Number(this.currentEmployee.id)
            || data.type !== this.evaluationType || !Array.isArray(data.classes) || !data.summary) {
          throw new Error('Invalid employee evaluation KPI response')
        }
        this.evaluationData = data
      } catch (error) {
        if (requestId !== this._evaluationRequestId) return
        this.evaluationError = error.response && error.response.status === 422
          ? 'Thiếu lịch sử học sinh/lớp tại ngày chốt kỳ; chưa thể xác nhận số liệu.'
          : 'Không tải được KPI nhận xét cho nhân viên và kỳ đã chọn.'
      } finally {
        if (requestId === this._evaluationRequestId) this.loadingEvaluation = false
      }
    }
  }
}
</script>

<style scoped>
.personnel-kpi-container {
  padding: 24px;
  border: 1px solid #dce5f5;
  border-radius: 20px;
  background: #fff;
  color: #233044;
}
.module-header, .header-main, .title-line, .scope-filter, .panel-heading { display: flex; align-items: center; }
.module-header, .panel-heading { justify-content: space-between; gap: 16px; }
.module-header { padding-bottom: 20px; border-bottom: 1px solid #e7edf7; }
.header-main { gap: 14px; min-width: 0; }
.module-icon, .source-icon, .filter-icon { display: inline-flex; align-items: center; justify-content: center; flex: none; }
.module-icon { width: 46px; height: 46px; border-radius: 14px; color: #0f766e; background: #e4f7f2; font-size: 22px; }
.title-line { gap: 10px; flex-wrap: wrap; }
h2, h3, p { margin: 0; }
h2 { font-size: 20px; line-height: 1.3; font-weight: 700; }
h3 { font-size: 15px; font-weight: 700; }
.module-header p, .panel-heading p { margin-top: 3px; color: #7b8ba4; font-size: 13px; }
.live-data-badge { padding: 6px 10px; border-radius: 999px; color: #16875e; background: #e4f8ee; font-size: 11px; font-weight: 700; white-space: nowrap; }
.scope-filter { gap: 10px; flex: none; }
.scope-label { color: #5c6b82; font-size: 12px; font-weight: 700; white-space: nowrap; }
.scope-options { display: inline-flex; gap: 2px; padding: 3px; border-radius: 11px; background: #e9eef7; }
.scope-button { min-width: 52px; padding: 6px 11px; border: 0; border-radius: 8px; background: transparent; color: #53627a; cursor: pointer; font: inherit; font-size: 12px; font-weight: 700; }
.scope-button.active { background: #fff; color: #345be7; box-shadow: 0 1px 4px rgba(30, 55, 110, .13); }
.evaluation-filter { display: inline-flex; gap: 2px; padding: 3px; border-radius: 11px; background: #e9eef7; }
.evaluation-filter-button { padding: 6px 10px; border: 0; border-radius: 8px; background: transparent; color: #53627a; cursor: pointer; font: inherit; font-size: 11px; font-weight: 700; white-space: nowrap; }
.evaluation-filter-button.active { background: #fff; color: #6e48c9; box-shadow: 0 1px 4px rgba(30, 55, 110, .13); }
.period-picker { display: flex; align-items: center; gap: 6px; }
.evaluation-period-picker { display: flex; align-items: center; gap: 6px; }
.period-picker ::v-deep .el-date-editor { width: 168px; }
.evaluation-period-picker ::v-deep .el-date-editor { width: 168px; }
.evaluation-period-picker ::v-deep .el-input-number { width: 110px; }
.period-picker ::v-deep .el-date-editor.el-range-editor { width: 260px; }
.period-picker ::v-deep .el-input__inner { border-radius: 8px; }
.evaluation-period-picker ::v-deep .el-input__inner { border-radius: 8px; }
.person-filter { display: flex; align-items: center; gap: 20px; margin-top: 20px; padding: 16px 18px; border: 1px solid #dce5f5; border-radius: 16px; background: #fbfcff; }
.filter-intro { display: flex; align-items: center; gap: 11px; flex: 1; min-width: 200px; }
.filter-icon { width: 38px; height: 38px; border-radius: 11px; color: #356af3; background: #eaf0ff; font-size: 18px; }
.filter-intro p { margin-top: 3px; color: #8492a8; font-size: 12px; }
.filter-controls { display: flex; gap: 12px; }
.filter-field { display: flex; flex-direction: column; gap: 5px; min-width: 185px; color: #65748a; font-size: 11px; font-weight: 700; }
.filter-field ::v-deep .el-select { width: 100%; }
.employee-field { min-width: 220px; }
.selected-person { display: flex; align-items: center; gap: 8px; min-width: 170px; padding-left: 16px; border-left: 1px solid #e0e8f4; }
.person-avatar { display: inline-flex; align-items: center; justify-content: center; width: 34px; height: 34px; flex: none; border-radius: 50%; color: #3157ce; background: #e7edff; font-size: 11px; font-weight: 800; }
.selected-person > span:last-child { display: flex; flex-direction: column; gap: 3px; }
.selected-person strong { font-size: 12px; white-space: nowrap; }
.selected-person small { color: #8997aa; font-size: 11px; }
.source-panel { margin-top: 20px; }
.chart-panel { margin-top: 20px; padding: 20px; border: 1px solid #dce5f5; border-radius: 17px; background: #fbfcff; }
.source-grid { display: flex; gap: 12px; margin-top: 12px; overflow-x: auto; padding: 1px 1px 5px; }
.source-card { display: flex; flex: 1 0 0; align-items: center; gap: 10px; min-width: 168px; min-height: 72px; padding: 11px 12px; border: 1px solid #dce5f5; border-left-width: 3px; border-radius: 15px; background: #fbfcff; color: inherit; cursor: pointer; text-align: left; font: inherit; transition: border-color .15s, box-shadow .15s, background .15s; }
.source-card:hover { border-color: #7caaa2; }
.source-card.selected { background: #f4fffb; box-shadow: 0 0 0 2px rgba(15, 155, 131, .14); }
.source-icon { width: 38px; height: 38px; border-radius: 11px; font-size: 18px; }
.source-copy { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.source-copy strong { font-size: 12px; white-space: nowrap; }
.source-copy small { color: #8492a8; font-size: 11px; line-height: 1.3; white-space: nowrap; }
.discussion-badge { padding: 6px 10px; color: #a26510; background: #fff3df; }
.chart-heading-actions { display: flex; align-items: center; justify-content: flex-end; flex-wrap: wrap; gap: 10px; }
.chart-type-options { display: inline-flex; gap: 2px; padding: 3px; border-radius: 10px; background: #e9eef7; }
.chart-type-options button { padding: 6px 10px; border: 0; border-radius: 8px; background: transparent; color: #64748b; cursor: pointer; font: inherit; font-size: 11px; font-weight: 700; }
.chart-type-options button.active { background: #fff; color: #345be7; box-shadow: 0 1px 4px rgba(30, 55, 110, .13); }
.chart-caption { display: flex; flex-direction: column; gap: 4px; }
.chart-caption strong { font-size: 13px; }
.chart-caption span { color: #8a98ad; font-size: 11px; }
.class-chart-layout { display: grid; grid-template-columns: minmax(0, 1.5fr) minmax(280px, 1fr); gap: 16px; margin-top: 17px; }
.ratio-class-panel { min-width: 0; margin-top: 17px; padding: 18px; border: 1px solid #e0e8f4; border-radius: 13px; background: #fff; }
.ratio-class-scroll { display: flex; gap: 16px; margin-top: 16px; padding: 3px 2px 14px; overflow-x: auto; scroll-snap-type: x mandatory; }
.ratio-class-card { display: flex; flex: 1 0 300px; flex-direction: column; align-items: center; gap: 9px; max-width: 560px; padding: 20px 14px; border: 1px solid #e2eaf6; border-radius: 14px; background: #fbfcff; text-align: center; scroll-snap-align: start; }
.ratio-class-card:first-child { margin-left: auto; }
.ratio-class-card:last-child { margin-right: auto; }
.ratio-class-name { margin-top: 2px; font-size: 13px; }
.ratio-class-count { color: #64748b; font-size: 11px; white-space: nowrap; }
.ratio-class-unmarked { color: #8997aa; font-size: 11px; }
.ratio-class-empty { display: flex; min-height: 210px; flex-direction: column; align-items: center; justify-content: center; gap: 10px; color: #8492a8; }
.ratio-class-empty > i { display: grid; width: 42px; height: 42px; place-items: center; border-radius: 12px; background: #eef3fb; color: #8294af; font-size: 19px; }
.ratio-class-empty strong { color: #344157; font-size: 13px; }
.ratio-class-empty span { font-size: 11px; }
.class-chart-main, .class-list, .attendance-chart { min-width: 0; padding: 18px; border: 1px solid #e0e8f4; border-radius: 13px; background: #fff; }
.class-bars { display: flex; align-items: end; justify-content: space-around; gap: 12px; min-height: 248px; margin-top: 14px; padding: 15px 10px 0; border-bottom: 1px solid #dce5f1; }
.class-bar-item { display: flex; flex: 1; flex-direction: column; align-items: center; justify-content: end; gap: 8px; max-width: 120px; min-width: 55px; cursor: pointer; }
.class-bar-item.read-only { cursor: default; }
.class-bar-item > strong { font-size: 13px; }
.class-bar-item > small { height: 28px; color: #64748b; font-size: 11px; font-weight: 700; text-align: center; }
.bar-track { position: relative; width: min(42px, 100%); height: 176px; }
.bar-value { position: absolute; right: 0; bottom: 0; left: 0; border-radius: 8px 8px 0 0; }
.bar-value { min-height: 5px; transition: height .2s ease; }
.class-list h4 { display: flex; justify-content: space-between; margin: 0 0 12px; font-size: 13px; }
.class-list h4 span { color: #8a98ad; font-size: 11px; font-weight: 500; }
.class-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; width: 100%; margin-top: 7px; padding: 12px; border: 1px solid #e3eaf5; border-radius: 10px; background: #fbfcff; color: #344157; cursor: pointer; font: inherit; text-align: left; }
.class-row.active { border-color: #82a5ef; background: #f1f6ff; }
.class-row.read-only { cursor: default; }
.class-row-name { font-size: 12px; font-weight: 700; white-space: nowrap; }
.class-row-values { display: flex; flex-direction: column; align-items: flex-end; gap: 3px; }
.class-row-values small { color: #718096; font-size: 10px; }
.class-color-dot { display: inline-block; width: 9px; height: 9px; margin-right: 5px; border-radius: 3px; }
.class-row strong { font-size: 12px; white-space: nowrap; }
.menu-kpi-total { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-top: 12px; padding: 12px 14px; border: 1px solid #f4dfc8; border-radius: 10px; background: #fff9f2; }
.menu-kpi-total span { color: #8b6b4f; font-size: 11px; }
.menu-kpi-total strong { color: #a95612; font-size: 15px; white-space: nowrap; }
.class-note { margin-top: 13px; color: #8a98ad; font-size: 11px; line-height: 1.5; }
.data-warning { color: #a15c12; }
.pie-area { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 245px; gap: 12px; }
.pie-visual { position: relative; display: flex; align-items: center; justify-content: center; width: min(100%, 380px); height: 280px; }
.pie-slice-label { position: absolute; z-index: 1; min-width: 84px; padding: 6px 8px; border: 1px solid; border-radius: 8px; background: #fff; box-shadow: 0 2px 8px rgba(39, 64, 109, .12); color: #3c4b61; font-size: 11px; text-align: center; white-space: nowrap; transform: translate(-50%, -50%); }
.pie-slice-label strong { font-size: 12px; }
.donut { display: flex; align-items: center; justify-content: center; width: 188px; height: 188px; border-radius: 50%; }
.donut-hole { display: flex; flex-direction: column; align-items: center; justify-content: center; width: 128px; height: 128px; border-radius: 50%; background: #fff; }
.donut-hole strong { font-size: 29px; line-height: 1.1; }
.donut-hole small { margin-top: 5px; color: #8a98ad; font-size: 11px; }
.donut.class-donut { width: 174px; height: 174px; }
.donut-hole.class-donut-hole { width: 118px; height: 118px; }
.class-donut-hole strong { font-size: 27px; }
.pie-caption { color: #64748b; font-size: 12px; font-weight: 700; text-align: center; }
.attendance-chart { min-width: 0; margin-top: 17px; padding: 18px; border: 1px solid #e0e8f4; border-radius: 13px; background: #fff; }
.attendance-load-state { display: flex; align-items: center; justify-content: center; gap: 8px; min-height: 112px; color: #8291a8; font-size: 12px; text-align: center; }
.attendance-load-state > i { color: #7890b2; font-size: 16px; }
.attendance-load-state.error-state, .attendance-load-state.error-state > i { color: #d34c5b; }
.attendance-overview { display: grid; grid-template-columns: 200px minmax(0, 1fr); gap: 14px; max-width: 1080px; margin: 17px auto 0; }
.attendance-donut-card { display: flex; align-items: center; justify-content: center; min-height: 182px; border: 1px solid #e2eaf6; border-radius: 12px; background: #fbfcff; }
.attendance-donut-wrap { position: relative; width: 150px; height: 150px; }
.attendance-donut-card .donut { width: 150px; height: 150px; }
.attendance-donut-card .donut-hole { width: 102px; height: 102px; }
.attendance-donut-card .donut-hole strong { font-size: 25px; }
.donut-percent-label { position: absolute; z-index: 2; transform: translate(-50%, -50%); color: #34445b; font-size: 10px; font-weight: 800; line-height: 1; pointer-events: none; white-space: nowrap; text-shadow: 0 1px 2px rgba(255, 255, 255, .7); }
.donut-percent-label.present, .donut-percent-label.unpermitted { color: #fff; text-shadow: 0 1px 2px rgba(30, 41, 59, .35); }
.donut-percent-label.permitted { color: #68440b; }
.attendance-summary-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 8px; }
.attendance-summary-item, .attendance-extra-item { display: flex; flex-direction: column; justify-content: center; align-items: flex-start; gap: 3px; min-width: 0; padding: 9px 11px; border: 1px solid #e2eaf6; border-radius: 10px; background: #fbfcff; color: #59687f; font-size: 11px; line-height: 1.35; }
.attendance-summary-item strong, .attendance-extra-item strong { margin-left: auto; color: #233044; white-space: nowrap; }
.attendance-summary-item.present { border-color: #d8eee5; background: #f2faf6; }
.attendance-summary-item.permitted { border-color: #f6e8c9; background: #fffaf0; }
.attendance-summary-item.unpermitted { border-color: #f5dadd; background: #fff6f6; }
.attendance-summary-item.unmarked { border-color: #dce4ed; background: #f6f8fb; }
.attendance-summary-item.off { border-color: #e0e6ee; background: #f7f9fc; }
.attendance-summary-item.upcoming { border-color: #dce8fb; background: #f5f8ff; }
.attendance-extra-item { position: relative; padding-left: 32px; cursor: pointer; font: inherit; font-size: 11px; line-height: 1.35; text-align: left; transition: border-color .15s, background .15s; }
.attendance-extra-item.late { border-color: #f6e8c9; background: #fffaf0; }
.attendance-extra-item.early { border-color: #dce8fb; background: #f5f8ff; }
.attendance-extra-item > span { font-size: 11px; }
.attendance-extra-item strong { font-size: 11px; line-height: 1.35; }
.attendance-extra-item.late:hover { border-color: #e8c57d; background: #fff5df; }
.attendance-extra-item.early:hover { border-color: #b9cdf4; background: #edf3ff; }
.attendance-extra-item:focus-visible { outline: 2px solid #668be8; outline-offset: 2px; }
.attendance-extra-item i { color: #dd8b28; font-size: 13px; }
.attendance-extra-item > i { position: absolute; top: 10px; left: 11px; }
.attendance-extra-item small { align-self: flex-end; color: #8a98ad; font-size: 10px; line-height: 1.3; }
.attendance-calendar-note { max-width: 1080px; margin: 8px auto 0; color: #8997aa; font-size: 11px; }
.legend-dot { display: inline-block; width: 9px; height: 9px; flex: none; border-radius: 50%; }
.legend-dot.present { background: #20a779; }
.legend-dot.permitted { background: #f59e0b; }
.legend-dot.unpermitted { background: #ef5b68; }
.legend-dot.unmarked { background: #93a5bc; }
.legend-dot.off { background: #cbd5e1; }
.legend-dot.upcoming { background: #d8e6ff; }
.legend-dot.unknown { background: #a78bfa; }
.attendance-timeline { max-width: 1080px; margin: 16px auto 0; padding: 12px; border: 1px solid #e2eaf6; border-radius: 12px; background: #fbfcff; }
.timeline-heading { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; }
.timeline-heading strong { font-size: 13px; }
.timeline-heading span { color: #8997aa; font-size: 11px; }
.timeline-scroll { display: flex; gap: 8px; margin-top: 12px; padding-bottom: 5px; overflow-x: auto; }
.timeline-day { display: flex; flex: 0 0 95px; flex-direction: column; align-items: center; gap: 7px; padding: 10px 7px; border: 1px solid #e0e8f4; border-radius: 10px; background: #fff; color: #46566d; cursor: pointer; font: inherit; }
.timeline-day.present { border-color: #d7eee4; background: #f4fbf7; }
.timeline-day.permitted { border-color: #f4e6c7; background: #fffaf1; }
.timeline-day.unpermitted { border-color: #f3dadd; background: #fff6f6; }
.timeline-day.unmarked { border-color: #dce4ed; background: #f6f8fb; }
.timeline-day.off { border-color: #e1e7ef; background: #f7f9fc; }
.timeline-day.upcoming { border-color: #e1e9f8; background: #f7f9fe; }
.timeline-day.unknown { border-color: #e8defd; background: #faf7ff; }
.timeline-day:first-child { margin-left: auto; }
.timeline-day:last-child { margin-right: auto; }
.timeline-day.selected, .timeline-day.selected.present, .timeline-day.selected.permitted, .timeline-day.selected.unpermitted, .timeline-day.selected.unmarked, .timeline-day.selected.off, .timeline-day.selected.upcoming { border-color: #6188e9; background: #eef4ff; box-shadow: 0 0 0 2px rgba(53, 106, 243, .1); }
.timeline-day > span { font-size: 11px; font-weight: 700; }
.timeline-day small { color: #8997aa; font-size: 10px; white-space: nowrap; }
.attendance-detail { max-width: 1080px; margin: 16px auto 0; padding: 14px; border: 1px solid #dce5f5; border-radius: 12px; background: #fbfcff; }
.attendance-detail-heading { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.attendance-detail-heading > div { display: flex; flex-direction: column; gap: 3px; }
.attendance-detail-heading strong { font-size: 14px; }
.attendance-detail-heading div span { color: #8a98ad; font-size: 11px; }
.attendance-status-badge { padding: 6px 10px; border-radius: 999px; font-size: 11px; font-weight: 700; white-space: nowrap; }
.attendance-status-badge.present { color: #16875e; background: #e2f8ee; }
.attendance-status-badge.permitted { color: #a56708; background: #fff2d8; }
.attendance-status-badge.unpermitted { color: #c74453; background: #fff0f1; }
.attendance-status-badge.unmarked { color: #61748c; background: #eaf0f6; }
.attendance-status-badge.off { color: #64748b; background: #edf1f6; }
.attendance-status-badge.unknown { color: #7352bd; background: #f2ecff; }
.attendance-detail-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 8px; margin-top: 12px; }
.attendance-detail-grid > div { display: flex; flex-direction: column; gap: 4px; padding: 10px 11px; border: 1px solid #e2eaf6; border-radius: 9px; background: #fff; }
.attendance-detail-grid span, .attendance-sessions span { color: #8190a5; font-size: 11px; }
.attendance-detail-grid strong { font-size: 15px; }
.attendance-sessions { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; margin-top: 8px; }
.attendance-sessions > div { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 8px 11px; border: 1px solid #e2eaf6; border-radius: 9px; background: #fff; }
.attendance-sessions strong { font-size: 12px; }
.attendance-detail > p { margin-top: 12px; color: #8a98ad; font-size: 11px; line-height: 1.5; }
.attendance-event-summary { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; margin-bottom: 16px; }
.attendance-event-summary > div { display: flex; flex-direction: column; gap: 6px; padding: 13px 15px; border: 1px solid #e2eaf6; border-radius: 11px; background: #f7f9fd; }
.attendance-event-summary > div.late { border-color: #f3e4c2; background: #fffaf0; }
.attendance-event-summary > div.early { border-color: #dce7fa; background: #f4f8ff; }
.attendance-event-summary small, .attendance-event-time small { color: #8492a8; font-size: 11px; }
.attendance-event-summary strong { color: #26364c; font-size: 20px; line-height: 1; }
.attendance-event-summary strong span { color: #73829a; font-size: 11px; font-weight: 600; }
.attendance-event-list { display: flex; flex-direction: column; gap: 8px; max-height: 330px; overflow: auto; }
.attendance-event-row { display: flex; align-items: center; gap: 12px; min-width: 0; padding: 11px 12px; border: 1px solid #e5ebf4; border-radius: 11px; background: #fff; }
.attendance-event-row.late { border-left: 3px solid #efa72f; }
.attendance-event-row.early { border-left: 3px solid #6d92e8; }
.attendance-event-index { display: grid; width: 34px; height: 34px; flex: none; place-items: center; border-radius: 9px; background: #f0f4fb; color: #7b8ba4; font-size: 11px; font-weight: 800; }
.attendance-event-date, .attendance-event-time { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.attendance-event-date { flex: 1; }
.attendance-event-date strong, .attendance-event-time strong { color: #2e3c52; font-size: 12px; }
.attendance-event-date small { color: #929fb1; font-size: 10px; }
.attendance-event-minutes { padding: 7px 9px; border-radius: 8px; background: #fff4df; color: #a66a0c; font-size: 11px; font-weight: 800; white-space: nowrap; }
.attendance-event-row.early .attendance-event-minutes { background: #edf3ff; color: #496fc5; }
.attendance-event-empty { display: flex; flex-direction: column; align-items: center; gap: 7px; padding: 30px 15px; border: 1px dashed #d9e3f1; border-radius: 12px; background: #fafcff; text-align: center; }
.attendance-event-empty > span { display: grid; width: 40px; height: 40px; place-items: center; border-radius: 50%; background: #e7f8ef; color: #24a06f; font-size: 18px; }
.attendance-event-empty strong { color: #2e3c52; font-size: 13px; }
.attendance-event-empty small { color: #8492a8; font-size: 11px; }
::v-deep .attendance-event-dialog { overflow: hidden; border-radius: 16px; }
::v-deep .attendance-event-dialog .el-dialog__header { padding: 18px 22px 14px; border-bottom: 1px solid #edf1f7; }
::v-deep .attendance-event-dialog .el-dialog__body { padding: 17px 22px 22px; }
::v-deep .attendance-event-dialog .el-dialog__headerbtn { top: 20px; right: 18px; }
.attendance-dialog-title { display: flex; align-items: center; gap: 11px; padding-right: 20px; }
.attendance-dialog-icon { display: grid; width: 38px; height: 38px; flex: none; place-items: center; border-radius: 11px; background: #fff2d9; color: #d98b17; font-size: 17px; }
.attendance-dialog-icon.early { background: #edf3ff; color: #5278cf; }
.attendance-dialog-title > div { display: flex; flex-direction: column; gap: 4px; }
.attendance-dialog-title strong { color: #26364c; font-size: 16px; }
.attendance-dialog-title small { color: #8795aa; font-size: 11px; font-weight: 400; }
.empty-classes { display: flex; flex-direction: column; align-items: center; gap: 8px; margin-top: 17px; padding: 55px 15px; border: 1px dashed #cddbf0; border-radius: 12px; background: #fff; text-align: center; }
.empty-classes i { color: #9aacc7; font-size: 28px; }
.empty-classes strong { font-size: 13px; }
.empty-classes span { color: #8a98ad; font-size: 12px; }
@media (max-width: 1400px) { .person-filter { flex-wrap: wrap; } .filter-intro { flex-basis: 100%; } .selected-person { margin-left: auto; } }
@media (max-width: 900px) { .class-chart-layout { grid-template-columns: 1fr; } .attendance-summary-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 700px) { .personnel-kpi-container { padding: 16px; } .module-header, .panel-heading { align-items: flex-start; flex-direction: column; } .person-filter { padding: 14px; } .filter-controls { flex-wrap: wrap; width: 100%; } .filter-field, .employee-field { flex: 1 1 100%; } .selected-person { margin-left: 0; padding-left: 0; border-left: 0; } .chart-panel { padding: 15px; } .chart-heading-actions { width: 100%; justify-content: flex-start; } .pie-slice-label { display: none; } .attendance-overview { grid-template-columns: 1fr; } .attendance-detail-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } .attendance-sessions { grid-template-columns: 1fr; } }
::v-deep .attendance-event-dialog { width: calc(100% - 24px) !important; max-width: 560px; }
@media (max-width: 520px) { .attendance-event-summary { grid-template-columns: 1fr; } .attendance-event-row { gap: 8px; padding: 9px; } .attendance-event-index { width: 30px; height: 30px; } .attendance-event-date strong, .attendance-event-time strong { font-size: 11px; } ::v-deep .attendance-event-dialog .el-dialog__header { padding: 15px 16px 12px; } ::v-deep .attendance-event-dialog .el-dialog__body { padding: 14px 16px 17px; } }
</style>
